// scheduling/components/dashboard/ClosedJobsSection.jsx
// Presentational: "Completed with scheduled time" — jobs PE has closed that still hold
// future-dated schedule entries, with a preview-then-confirm clear.
//
// Why this exists: peSync used to delete a completed job's rprs.Jobs row AND every schedule
// entry on it, which silently destroyed real future hours (84 entries / 287h across 8 staff
// when Ergon's 2024 1120 closed on 2026-08-26). It now retains the job and marks it closed
// instead — so the time survives, but it needs somewhere to be seen and deliberately cleared.
//
// Two modes:
//   'manager' — every staffer's hours on jobs the viewer leads (or firm-wide on All Jobs).
//               Clearing removes the whole job's future time.
//   'mine'    — only the viewer's own hours, and clearing is scoped to their rows.
//
// Past-dated entries are never offered for deletion: they are the job's history.

import { useState } from 'react';

const fmtDate = (d) => (d
  ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '—');

export default function ClosedJobsSection({
  closedJobs, clearClosedJobFuture, mode = 'manager', meStaffId, onNavigateJob,
}) {
  const [open, setOpen] = useState(true);
  const [pending, setPending] = useState(null); // { jobId, preview, busy }
  const [error, setError] = useState(null);

  // In 'mine' mode a job is only worth showing if the viewer personally has hours on it.
  const rows = (closedJobs || []).filter(j => (mode === 'mine' ? (j.MyFutureEntries || 0) > 0 : true));
  if (!rows.length) return null;

  const entriesOf = (j) => (mode === 'mine' ? j.MyFutureEntries : j.FutureEntries) || 0;
  const hoursOf = (j) => (mode === 'mine' ? j.MyFutureHours : j.FutureHours) || 0;

  const totalEntries = rows.reduce((a, j) => a + entriesOf(j), 0);
  const totalHours = Math.round(rows.reduce((a, j) => a + Number(hoursOf(j)), 0) * 100) / 100;

  const startPreview = async (job) => {
    setError(null);
    setPending({ jobId: job.JobId, preview: null, busy: true });
    try {
      const p = await clearClosedJobFuture(job.JobId, {
        preview: true,
        ...(mode === 'mine' ? { staffId: meStaffId } : {}),
      });
      setPending({ jobId: job.JobId, preview: p, busy: false });
    } catch (e) {
      setPending(null);
      setError(e.message || 'Could not read that job’s schedule');
    }
  };

  const confirm = async () => {
    setPending(p => ({ ...p, busy: true }));
    try {
      await clearClosedJobFuture(pending.jobId, mode === 'mine' ? { staffId: meStaffId } : {});
      setPending(null);
    } catch (e) {
      setPending(p => ({ ...p, busy: false }));
      setError(e.message || 'Could not clear that job’s schedule');
    }
  };

  return (
    <div style={{
      border: '1px solid #FDE68A', background: '#FFFBEB', borderRadius: 8,
      padding: '8px 10px', marginBottom: 12,
    }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%', background: 'none',
          border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
        }}>
        <span style={{ fontSize: 13 }}>&#9888;&#65039;</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#92400E' }}>
          Completed with scheduled time
        </span>
        <span style={{ fontSize: 11, color: '#B45309' }}>
          {rows.length} {rows.length === 1 ? 'job' : 'jobs'} &middot; {totalEntries} {totalEntries === 1 ? 'entry' : 'entries'} &middot; {totalHours}h
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#B45309' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, color: '#92400E', marginBottom: 6 }}>
            {mode === 'mine'
              ? 'These jobs are finished in PE but still hold time on your calendar. Clearing removes your future-dated entries only — past hours are kept.'
              : 'These jobs are finished in PE but still hold future-dated time. Clearing removes every future entry on the job, including dates outside the grid window. Past hours are kept.'}
          </div>

          {error && (
            <div style={{ fontSize: 11, color: '#B91C1C', marginBottom: 6 }}>{error}</div>
          )}

          {rows.map((j) => {
            const active = pending?.jobId === j.JobId ? pending : null;
            return (
              <div key={j.JobId} style={{
                borderTop: '1px solid #FDE68A', paddingTop: 6, marginTop: 6,
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span
                    onClick={onNavigateJob ? () => onNavigateJob(j.JobId) : undefined}
                    style={{
                      fontSize: 12, fontWeight: 700, color: '#78350F',
                      cursor: onNavigateJob ? 'pointer' : 'default',
                      textDecoration: onNavigateJob ? 'underline' : 'none',
                    }}>
                    {j.ClientName}
                  </span>
                  <span style={{ fontSize: 11, color: '#92400E' }}>{j.Name}</span>
                  <span style={{ fontSize: 10, color: '#B45309' }}>
                    completed {fmtDate(j.ClosedAt)}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: '#92400E', marginTop: 2 }}>
                  {entriesOf(j)} future {entriesOf(j) === 1 ? 'entry' : 'entries'} &middot; {hoursOf(j)}h
                  {mode === 'manager' && j.StaffCount > 1 ? ` · ${j.StaffCount} people` : ''}
                  {j.PastEntries > 0 ? ` · ${j.PastEntries} past ${j.PastEntries === 1 ? 'entry' : 'entries'} kept` : ''}
                </div>

                {!active && (
                  <button type="button" className="sched-closed-btn" style={{ marginTop: 5 }}
                    onClick={() => startPreview(j)}>
                    {mode === 'mine' ? 'Clear my future time…' : 'Clear future time…'}
                  </button>
                )}

                {active?.busy && !active.preview && (
                  <div style={{ fontSize: 11, color: '#92400E', marginTop: 5 }}>Checking&hellip;</div>
                )}

                {active?.preview && (
                  <div style={{ fontSize: 11, color: '#92400E', marginTop: 5 }}>
                    {active.preview.entries === 0 ? (
                      <>
                        <div>Nothing left to clear.</div>
                        <button type="button" className="sched-closed-btn" style={{ marginTop: 4 }}
                          onClick={() => setPending(null)}>Close</button>
                      </>
                    ) : (
                      <>
                        <div style={{ fontWeight: 700 }}>
                          Delete {active.preview.entries} {active.preview.entries === 1 ? 'entry' : 'entries'} ({active.preview.hours}h)
                          {mode === 'manager' && active.preview.staff.length > 1
                            ? ` across ${active.preview.staff.length} people` : ''}?
                        </div>
                        <div>
                          {String(active.preview.firstDate).slice(0, 10)} &ndash; {String(active.preview.lastDate).slice(0, 10)}
                        </div>
                        {mode === 'manager' && active.preview.staff.length > 1 && (
                          <div style={{ marginTop: 3, maxHeight: 90, overflowY: 'auto' }}>
                            {active.preview.staff.map(s => (
                              <div key={s.staffId}>{s.staffName} &middot; {s.entries} &middot; {s.hours}h</div>
                            ))}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                          <button type="button" className="sched-closed-btn sched-closed-btn--danger"
                            disabled={active.busy} onClick={confirm}>
                            {active.busy ? 'Deleting…' : 'Delete'}
                          </button>
                          <button type="button" className="sched-closed-btn"
                            disabled={active.busy} onClick={() => setPending(null)}>Cancel</button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
